import React from "react";
import { LuClock, LuUsers } from "react-icons/lu";

import Card from "../ui/Card";

// Shared presentational panel for BOTH tournament types -- callers normalize
// their own type's detail-GET response into this one shape so the rendering
// logic (and the "flat blinds"/"no break scheduled" empty states) isn't
// duplicated per type. No buy-in/payout section anywhere here, by design --
// see poker/README.md's "Product policy: no stakes".
//
// Shape:
// {
//   statusLabel: string,
//   startingStack: number,
//   registeredCount: number, maxEntrants: number,
//   eliminatedCount: number | null,   // null where the concept doesn't apply (Single Table)
//   tableCount: number | null,        // null for Single Table (always exactly one table)
//   currentSmallBlind: number, currentBigBlind: number, currentAnte: number | null,
//   levelSecondsRemaining: number | null,   // null = no live countdown right now
//   levelEmptyMessage: string | null,       // shown instead of a countdown when
//                                            // levelSecondsRemaining is null -- the
//                                            // caller picks the right message for
//                                            // its own context (genuinely flat
//                                            // blinds vs. a real schedule that just
//                                            // hasn't started ticking yet), since
//                                            // "null" alone can't tell those apart.
//   breakStatus: { on_break, seconds_remaining, next_break_after_level } | null, // MTT only
//   scheduledStartLabel: string | null,     // Single Table only
//   isPastScheduledStart: boolean | null,   // Single Table only
// }
function formatCountdown(seconds) {
  if (seconds === null || seconds === undefined) return null;
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function TournamentInfoPanel({ data, loading }) {
  if (loading && !data) {
    return (
      <Card variant="outline" className="mt-3 grid min-h-[100px] place-items-center p-4 text-xs text-text-secondary">
        Loading details...
      </Card>
    );
  }
  if (!data) return null;

  const {
    statusLabel,
    startingStack,
    registeredCount,
    maxEntrants,
    eliminatedCount,
    tableCount,
    currentSmallBlind,
    currentBigBlind,
    currentAnte,
    levelSecondsRemaining,
    levelEmptyMessage,
    breakStatus,
    scheduledStartLabel,
    isPastScheduledStart,
  } = data;

  return (
    <Card variant="outline" className="mt-3 flex flex-col gap-3 p-3 text-xs text-text-secondary sm:p-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <InfoTile label="Status">
          <span className="capitalize text-text-primary">{statusLabel}</span>
          {scheduledStartLabel ? (
            <div className="mt-0.5 text-[10px] text-text-faint">
              {isPastScheduledStart ? "Past scheduled start" : `Starts ${scheduledStartLabel}`}
            </div>
          ) : null}
        </InfoTile>

        <InfoTile label="Starting Stack">
          <span className="text-text-primary">{startingStack}</span>
        </InfoTile>

        <InfoTile label="Players">
          <div className="flex items-center gap-1 text-text-primary">
            <LuUsers size={13} className="text-brand-emerald" />
            {registeredCount}/{maxEntrants}
          </div>
          {eliminatedCount !== null && eliminatedCount !== undefined ? (
            <div className="mt-0.5 text-[10px] text-text-faint">
              {registeredCount - eliminatedCount} remaining &middot; {eliminatedCount} eliminated
            </div>
          ) : null}
        </InfoTile>

        {tableCount !== null && tableCount !== undefined ? (
          <InfoTile label="Tables">
            <span className="text-text-primary">{tableCount}</span>
          </InfoTile>
        ) : null}

        <InfoTile label="Current Blinds">
          <span className="text-text-primary">
            {currentSmallBlind}/{currentBigBlind}
            {currentAnte ? ` (ante ${currentAnte})` : ""}
          </span>
        </InfoTile>

        <InfoTile label={levelSecondsRemaining === null ? "Blind Schedule" : "Next Level In"}>
          {levelSecondsRemaining === null ? (
            <span className="text-text-primary">{levelEmptyMessage}</span>
          ) : (
            <div className="flex items-center gap-1 text-text-primary">
              <LuClock size={13} className="text-brand-cyan" />
              {formatCountdown(levelSecondsRemaining)}
            </div>
          )}
        </InfoTile>
      </div>

      {breakStatus ? (
        <div className="rounded-lg border border-border-soft bg-slate-950/40 p-2">
          {breakStatus.on_break ? (
            <span className="text-brand-amber">
              On break -- back in {formatCountdown(breakStatus.seconds_remaining)}
            </span>
          ) : breakStatus.next_break_after_level ? (
            <span>Next break after level {breakStatus.next_break_after_level}</span>
          ) : (
            <span>No breaks remaining in the schedule</span>
          )}
        </div>
      ) : null}
    </Card>
  );
}

function InfoTile({ label, children }) {
  return (
    <div className="rounded-lg border border-border-soft bg-slate-950/40 p-2">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-faint">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
